import { useEffect, useState } from "react";
import { Menu, useMantineColorScheme } from "@mantine/core";
import { useColorScheme } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconHome2,
  IconInfoCircle,
  IconLayoutGrid,
  IconLogin2,
  IconLogout,
  IconMoon,
  IconSettings,
  IconSun,
  IconUserCircle,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { GuardedNavLink } from "../../lib/navGuard";
import { useAuth } from "../../lib/authContext";
import { SignInModal } from "../../common/SignInModal";
import markUrl from "../../assets/nifusion-mark.png";

type LinkDef = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number | string; stroke?: number }>;
};

const links: LinkDef[] = [
  { to: "/", label: "Home", icon: IconHome2 },
  { to: "/apps", label: "Apps", icon: IconLayoutGrid },
  { to: "/about", label: "About", icon: IconInfoCircle },
];

function Avatar({ photoURL, name }: { photoURL?: string | null; name: string }) {
  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt=""
        referrerPolicy="no-referrer"
        className="h-7 w-7 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--color-accent)] text-xs font-semibold text-[color:var(--color-bg)]">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

export function Header() {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const preferred = useColorScheme();
  const location = useLocation();
  const { user, username, configured, signOutUser } = useAuth();
  const [signInOpen, setSignInOpen] = useState(false);

  // Reset hover/menu state on route change (harmless even without explicit state).
  useEffect(() => {}, [location.pathname]);

  const isDark = (colorScheme === "auto" ? preferred : colorScheme) === "dark";
  const displayName = username
    ? `@${username}`
    : user?.displayName || user?.email || "Account";

  const handleSignIn = () => {
    if (!configured) {
      notifications.show({
        title: "Sign-in isn't set up yet",
        message:
          "Sign-in will work once the Firebase keys are added. For now your data stays on this device.",
      });
      return;
    }
    setSignInOpen(true);
  };

  // Settings dropdown - currently houses just the theme toggle, but built to
  // grow as we add more (sync prefs, motion, etc.).
  const settingsMenu = (
    placement: "desktop" | "mobile"
  ) => (
    <Menu
      position={placement === "desktop" ? "bottom-end" : "top-end"}
      width={224}
      shadow="md"
    >
      <Menu.Target>
        <button
          type="button"
          aria-label="Settings"
          className={[
            "relative flex flex-col items-center justify-center gap-0.5 text-[color:var(--color-fg-muted)] transition-colors hover:text-[color:var(--color-fg)]",
            placement === "desktop" ? "w-20" : "h-full flex-1",
          ].join(" ")}
        >
          <IconSettings size={18} />
          <span className="text-[10px] font-medium">Settings</span>
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Appearance</Menu.Label>
        <Menu.Item
          leftSection={
            isDark ? <IconSun size={15} /> : <IconMoon size={15} />
          }
          onClick={() => toggleColorScheme()}
        >
          Switch to {isDark ? "light" : "dark"} mode
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  const signInPillButton = (
    <button
      type="button"
      onClick={handleSignIn}
      className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--color-accent)] px-3 py-1 text-xs font-semibold text-[color:var(--color-bg)] transition-colors hover:bg-[color:var(--color-accent-strong)]"
    >
      <IconLogin2 size={14} />
      Sign in
    </button>
  );

  const desktopAccountMenu = user && (
    <Menu position="bottom-end" width={232} shadow="md">
      <Menu.Target>
        <button
          type="button"
          aria-label="Account menu"
          className="rounded-full ring-offset-2 ring-offset-transparent transition hover:ring-2 hover:ring-[color:var(--color-accent)]"
        >
          <Avatar photoURL={user.photoURL} name={displayName} />
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <div className="px-3 py-2">
          <div className="truncate text-sm font-medium text-[color:var(--color-fg)]">
            {username ? `@${username}` : user.displayName || "Signed in"}
          </div>
          {user.email && (
            <div className="truncate text-xs text-[color:var(--color-fg-muted)]">
              {user.email}
            </div>
          )}
        </div>
        <Menu.Divider />
        <Menu.Item
          component={Link}
          to="/profile"
          leftSection={<IconUserCircle size={15} />}
        >
          Profile
        </Menu.Item>
        <Menu.Item
          leftSection={<IconLogout size={15} />}
          onClick={() => signOutUser()}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  // ----- Desktop: horizontal top tab bar, same icon-and-label rhythm as the
  // mobile bottom tab. Brand on the left, tab items in the middle, theme +
  // auth on the right. Active route gets an accent underline, mirroring the
  // mobile bar's accent overline. ------------------------------------------

  function DesktopTabItem({
    to,
    label,
    Icon,
  }: {
    to: string;
    label: string;
    Icon: LinkDef["icon"];
  }) {
    return (
      <GuardedNavLink
        to={to}
        end={to === "/"}
        className={({ isActive }) =>
          [
            "relative flex h-full w-20 flex-col items-center justify-center gap-0.5 transition-colors",
            isActive
              ? "text-[color:var(--color-accent)]"
              : "text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]",
          ].join(" ")
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden
                className="absolute inset-x-4 bottom-0 h-[3px] rounded-t-full bg-[color:var(--color-accent)]"
              />
            )}
            <Icon size={18} stroke={2} />
            <span className="text-[10px] font-medium">{label}</span>
          </>
        )}
      </GuardedNavLink>
    );
  }

  const desktopTopBar = (
    <header className="sticky top-0 z-40 hidden h-16 border-b border-[color:var(--color-border)] bg-[color:var(--color-bg)]/92 backdrop-blur sm:flex">
      <div className="mx-auto flex h-full w-full max-w-6xl items-stretch px-4">
        <GuardedNavLink
          to="/"
          aria-label="Nifusion.dev home"
          className="flex items-center gap-2 pr-4 text-[color:var(--color-fg)]"
        >
          <img src={markUrl} alt="" className="h-7 w-7" />
          <span className="text-base font-bold tracking-tight">
            nifusion<span className="text-[color:var(--color-accent)]">.dev</span>
          </span>
        </GuardedNavLink>

        <nav
          aria-label="Primary"
          className="flex items-stretch"
        >
          {links.map((l) => (
            <DesktopTabItem
              key={l.to}
              to={l.to}
              label={l.label}
              Icon={l.icon}
            />
          ))}
        </nav>

        <div className="ml-auto flex items-stretch">
          {settingsMenu("desktop")}

          {user ? (
            <div className="flex w-20 items-center justify-center">
              {desktopAccountMenu}
            </div>
          ) : (
            <div className="flex items-center justify-center pl-2">
              {signInPillButton}
            </div>
          )}
        </div>
      </div>
    </header>
  );

  // ----- Mobile: bottom tab bar (icon + tiny label) -----
  function MobileTabItem({
    to,
    label,
    Icon,
  }: {
    to: string;
    label: string;
    Icon: LinkDef["icon"];
  }) {
    return (
      <GuardedNavLink
        to={to}
        end={to === "/"}
        className={({ isActive }) =>
          [
            "relative flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors",
            isActive
              ? "text-[color:var(--color-accent)]"
              : "text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]",
          ].join(" ")
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden
                className="absolute inset-x-3 top-0 h-[3px] rounded-b-full bg-[color:var(--color-accent)]"
              />
            )}
            <Icon size={18} stroke={2} />
            <span className="text-[10px] font-medium">{label}</span>
          </>
        )}
      </GuardedNavLink>
    );
  }

  const mobileAccountTab = user ? (
    <Menu position="top-end" width={232} shadow="md">
      <Menu.Target>
        <button
          type="button"
          aria-label="Account menu"
          className="relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[color:var(--color-fg-muted)] transition-colors hover:text-[color:var(--color-fg)]"
        >
          <Avatar photoURL={user.photoURL} name={displayName} />
          <span className="max-w-[60px] truncate text-[10px] font-medium">
            {username
              ? `@${username}`
              : user.displayName?.split(" ")[0] || "You"}
          </span>
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        <div className="px-3 py-2">
          <div className="truncate text-sm font-medium text-[color:var(--color-fg)]">
            {username ? `@${username}` : user.displayName || "Signed in"}
          </div>
          {user.email && (
            <div className="truncate text-xs text-[color:var(--color-fg-muted)]">
              {user.email}
            </div>
          )}
        </div>
        <Menu.Divider />
        <Menu.Item
          component={Link}
          to="/profile"
          leftSection={<IconUserCircle size={15} />}
        >
          Profile
        </Menu.Item>
        <Menu.Item
          leftSection={<IconLogout size={15} />}
          onClick={() => signOutUser()}
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  ) : (
    <button
      type="button"
      onClick={handleSignIn}
      aria-label="Sign in"
      className="relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[color:var(--color-fg-muted)] transition-colors hover:text-[color:var(--color-accent)]"
    >
      <IconLogin2 size={18} stroke={2} />
      <span className="text-[10px] font-medium">Sign in</span>
    </button>
  );

  const mobileTabBar = (
    <nav
      aria-label="Primary navigation"
      className="fixed inset-x-0 bottom-0 z-40 flex h-16 border-t border-[color:var(--color-border)] bg-[color:var(--color-bg)]/95 backdrop-blur sm:hidden"
    >
      {links.map((l) => (
        <MobileTabItem key={l.to} to={l.to} label={l.label} Icon={l.icon} />
      ))}
      {settingsMenu("mobile")}
      {mobileAccountTab}
    </nav>
  );

  return (
    <>
      {desktopTopBar}
      {mobileTabBar}
      <SignInModal
        opened={signInOpen}
        onClose={() => setSignInOpen(false)}
      />
    </>
  );
}
